import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Spinner } from '@/components/ui/Spinner'
import { Breadcrumb } from '@/components/ui/Breadcrumb'
import { Badge } from '@/components/ui/Badge'
import { Avatar } from '@/components/ui/Avatar'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs'
import { Select } from '@/components/ui/Select'
import {
  ProgressTracker,
  DocumentsTab,
  TasksTab,
  ActivityTab,
  InvoicesQuotesTab,
  DesignTab,
  AssistanceTab,
  ContractTab,
  MembersTab,
} from '@/components/admin/projects'
import ProjectQuestionsTab from '@/components/admin/projects/ProjectQuestionsTab'
import { useProject, useUpdateProjectStatus, useUpdateProjectProgression } from '@/hooks/useProjects'
import { useProjectAssistanceRequests } from '@/hooks/useAssistance'
import { formatDate, formatCurrency } from '@/lib/utils'
import {
  ArrowLeft,
  Pencil,
  Calendar,
  DollarSign,
  User,
  Building2,
  Clock,
  AlertTriangle,
  LayoutList,
  FileText,
  Receipt,
  Activity,
  FolderKanban,
  Flag,
  Mail,
  HelpCircle,
  Palette,
  Wrench,
  ScrollText,
  Users,
} from 'lucide-react'
import type { ProjectStatus } from '@/types/project'
import { PROJECT_STATUS_CONFIG } from '@/types/project'

function ProjectStatusBadge({ status }: { status: ProjectStatus }) {
  const config = PROJECT_STATUS_CONFIG[status]
  return <Badge variant={config.color} size="md">{config.label}</Badge>
}

function OverviewTab({
  project,
  onStatusChange,
  onProgressionChange,
}: {
  project: NonNullable<ReturnType<typeof useProject>['data']>
  onStatusChange: (status: ProjectStatus) => void
  onProgressionChange: (value: number) => void
}) {
  const isOverdue =
    project.date_fin &&
    new Date(project.date_fin) < new Date() &&
    project.status !== 'completed' &&
    project.status !== 'cancelled'

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left column - Project info */}
      <div className="lg:col-span-2 space-y-6">
        {/* Metadata card */}
        <Card>
          <div className="p-6">
            <h3 className="font-semibold text-brown-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-mint-600" />
              Informations du projet
            </h3>

            <div className="space-y-4">
              {/* Description */}
              {project.description && (
                <div>
                  <label className="text-xs font-medium text-brown-500 uppercase tracking-wide">
                    Description
                  </label>
                  <p className="mt-1 text-brown-700 whitespace-pre-wrap">{project.description}</p>
                </div>
              )}

              {/* BMAD Document (from metadata - admin only) */}
              {(project as any).metadata?.bmad_markdown && (
                <div>
                  <details className="group">
                    <summary className="flex items-center gap-2 cursor-pointer text-xs font-medium text-brown-500 uppercase tracking-wide hover:text-mint-600 transition-colors">
                      <FileText className="w-4 h-4" />
                      Document BMAD (analyse IA)
                      <span className="text-[10px] font-normal normal-case text-brown-400 ml-1">(cliquer pour afficher)</span>
                    </summary>
                    <div className="mt-2 bg-beige-50 rounded-lg p-4 text-sm text-brown-700 max-h-96 overflow-y-auto border border-beige-200">
                      <pre className="whitespace-pre-wrap font-sans">{(project as any).metadata.bmad_markdown}</pre>
                    </div>
                  </details>
                </div>
              )}

              {/* Project metadata from database */}
              {(project as any).goals && (
                <div>
                  <label className="text-xs font-medium text-brown-500 uppercase tracking-wide">
                    Objectifs
                  </label>
                  <p className="mt-1 text-brown-700 whitespace-pre-wrap">{(project as any).goals}</p>
                </div>
              )}

              {(project as any).features && (
                <div>
                  <label className="text-xs font-medium text-brown-500 uppercase tracking-wide">
                    Fonctionnalités souhaitées
                  </label>
                  <p className="mt-1 text-brown-700 whitespace-pre-wrap">{(project as any).features}</p>
                </div>
              )}

              {(project as any).target && (
                <div>
                  <label className="text-xs font-medium text-brown-500 uppercase tracking-wide">
                    Public cible
                  </label>
                  <p className="mt-1 text-brown-700 whitespace-pre-wrap">{(project as any).target}</p>
                </div>
              )}

              {((project as any).site_type || (project as any).support_needed !== undefined) && (
                <div className="grid grid-cols-2 gap-4 pt-2 border-t border-beige-200">
                  {(project as any).site_type && (
                    <div>
                      <label className="text-xs font-medium text-brown-500 uppercase tracking-wide">
                        Type de projet
                      </label>
                      <p className="mt-1 text-brown-700 capitalize">{(project as any).site_type}</p>
                    </div>
                  )}

                  {(project as any).support_needed !== undefined && (
                    <div>
                      <label className="text-xs font-medium text-brown-500 uppercase tracking-wide">
                        Support requis
                      </label>
                      <p className="mt-1 text-brown-700">
                        {(project as any).support_needed ? 'Oui' : 'Non'}
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 pt-2 border-t border-beige-200">
                {/* Dates */}
                <div>
                  <label className="text-xs font-medium text-brown-500 uppercase">
                    Date de debut
                  </label>
                  <div className="mt-1 flex items-center gap-2 text-brown-700">
                    <Calendar className="w-4 h-4 text-brown-400" />
                    {project.date_debut
                      ? formatDate(project.date_debut)
                      : 'Non definie'}
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-brown-500 uppercase">
                    Date de fin
                  </label>
                  <div
                    className={`mt-1 flex items-center gap-2 ${
                      isOverdue ? 'text-red-600' : 'text-brown-700'
                    }`}
                  >
                    <Calendar className="w-4 h-4" />
                    {project.date_fin ? formatDate(project.date_fin) : 'Non definie'}
                    {isOverdue && <AlertTriangle className="w-4 h-4" />}
                  </div>
                </div>

                {/* Budget */}
                <div>
                  <label className="text-xs font-medium text-brown-500 uppercase">
                    Budget
                  </label>
                  <div className="mt-1 flex items-center gap-2 text-brown-700">
                    <DollarSign className="w-4 h-4 text-brown-400" />
                    {project.budget
                      ? formatCurrency(project.budget)
                      : 'Non defini'}
                  </div>
                </div>

                {/* Budget spent */}
                <div>
                  <label className="text-xs font-medium text-brown-500 uppercase">
                    Budget depense
                  </label>
                  <div className="mt-1 flex items-center gap-2 text-brown-700">
                    <DollarSign className="w-4 h-4 text-brown-400" />
                    {project.budget_spent
                      ? formatCurrency(project.budget_spent)
                      : formatCurrency(0)}
                    {project.budget && project.budget_spent && (
                      <span className="text-xs text-brown-500">
                        ({Math.round((project.budget_spent / project.budget) * 100)}%)
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Status selector */}
              <div>
                <label className="text-xs font-medium text-brown-500 uppercase">
                  Statut
                </label>
                <div className="mt-1">
                  <Select
                    value={project.status}
                    onChange={(e) => onStatusChange(e.target.value as ProjectStatus)}
                  >
                    {Object.entries(PROJECT_STATUS_CONFIG).map(([key, config]) => (
                      <option key={key} value={key}>
                        {config.label}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>

              {/* Progression slider */}
              <div>
                <label className="text-xs font-medium text-brown-500 uppercase">
                  Progression manuelle
                </label>
                <div className="mt-2 flex items-center gap-4">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={project.progression}
                    onChange={(e) => onProgressionChange(parseInt(e.target.value))}
                    className="flex-1 h-2 bg-beige-200 rounded-lg appearance-none cursor-pointer accent-mint-500"
                  />
                  <span className="text-sm font-medium text-brown-700 w-12 text-right">
                    {project.progression}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Client info card */}
        {project.client && (
          <Card>
            <div className="p-6">
              <h3 className="font-semibold text-brown-900 mb-4">Client</h3>

              <div className="flex items-start gap-4">
                <Avatar
                  src={project.client.avatar_url}
                  name={project.client.full_name}
                  size="lg"
                />
                <div className="flex-1">
                  <Link
                    to={`/clients/${project.client.id}`}
                    className="font-medium text-brown-900 hover:text-mint-600 transition-colors"
                  >
                    {project.client.full_name}
                  </Link>
                  {project.client.company && (
                    <div className="flex items-center gap-1.5 text-sm text-brown-600 mt-0.5">
                      <Building2 className="w-3.5 h-3.5" />
                      {project.client.company}
                    </div>
                  )}
                  <a
                    href={`mailto:${project.client.email}`}
                    className="flex items-center gap-1.5 text-sm text-brown-500 hover:text-mint-600 mt-1"
                  >
                    <Mail className="w-3.5 h-3.5" />
                    {project.client.email}
                  </a>
                </div>
                <Link to={`/clients/${project.client.id}`}>
                  <Button variant="outline" size="sm">
                    Voir le client
                  </Button>
                </Link>
              </div>
            </div>
          </Card>
        )}

        {/* Admin info card */}
        {project.admin && (
          <Card>
            <div className="p-6">
              <h3 className="font-semibold text-brown-900 mb-4">Responsable</h3>

              <div className="flex items-center gap-4">
                <Avatar
                  src={project.admin.avatar_url}
                  name={project.admin.full_name}
                  size="md"
                />
                <div>
                  <p className="font-medium text-brown-900">
                    {project.admin.full_name}
                  </p>
                  <p className="text-sm text-brown-500">{project.admin.email}</p>
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Right column - Progress tracker */}
      <div className="lg:col-span-1">
        <ProgressTracker
          projectId={project.id}
          status={project.status}
          progression={project.progression}
          milestones={project.milestones}
        />
      </div>
    </div>
  )
}

export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { data: project, isLoading, error } = useProject(id)
  const updateStatus = useUpdateProjectStatus()
  const updateProgression = useUpdateProjectProgression()
  const { data: assistanceRequests } = useProjectAssistanceRequests(id)
  const [activeTab, setActiveTab] = useState('overview')

  // Gérer l'onglet depuis l'URL
  useEffect(() => {
    const tabParam = searchParams.get('tab')
    if (tabParam && ['overview', 'tasks', 'documents', 'billing', 'design', 'questions', 'activity', 'assistance', 'contrat'].includes(tabParam)) {
      setActiveTab(tabParam)
    }
  }, [searchParams])

  const handleStatusChange = async (status: ProjectStatus) => {
    if (!id) return
    try {
      await updateStatus.mutateAsync({ id, status })
      toast.success('Statut mis à jour avec succès')
    } catch (error) {
      console.error('Error updating status:', error)
      toast.error('Erreur lors de la mise à jour du statut')
    }
  }

  const handleProgressionChange = async (progression: number) => {
    if (!id) return
    try {
      await updateProgression.mutateAsync({ id, progression })
      toast.success('Progression mise à jour avec succès')
    } catch (error) {
      console.error('Error updating progression:', error)
      toast.error('Erreur lors de la mise à jour de la progression')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }

  if (error || !project) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-brown-900 mb-2">
          Projet non trouve
        </h2>
        <p className="text-brown-600 mb-4">
          Le projet demande n'existe pas ou a ete supprime.
        </p>
        <Link to="/projects">
          <Button variant="outline">Retour a la liste</Button>
        </Link>
      </div>
    )
  }

  const isOverdue =
    project.date_fin &&
    new Date(project.date_fin) < new Date() &&
    project.status !== 'completed' &&
    project.status !== 'cancelled'

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb
        items={[
          { label: 'Projets', href: '/projects' },
          { label: project.nom },
        ]}
      />

      {/* Back button */}
      <Button
        variant="ghost"
        size="sm"
        leftIcon={<ArrowLeft className="w-4 h-4" />}
        onClick={() => navigate('/projects')}
      >
        Retour
      </Button>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-xl bg-mint-100 flex items-center justify-center flex-shrink-0">
            <FolderKanban className="w-7 h-7 text-mint-600" />
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-display font-bold text-brown-900">
                {project.nom}
              </h1>
              <ProjectStatusBadge status={project.status} />
              {isOverdue && (
                <Badge variant="red" size="sm">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  En retard
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-4 mt-2 text-sm text-brown-600">
              {project.client && (
                <span className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  {project.client.full_name}
                </span>
              )}
              {project.date_fin && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatDate(project.date_fin, { day: 'numeric', month: 'short' })}
                </span>
              )}
              {project.budget && (
                <span className="flex items-center gap-1">
                  <DollarSign className="w-4 h-4" />
                  {formatCurrency(project.budget)}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <Link to={`/projects/${project.id}/edit`}>
            <Button variant="outline" leftIcon={<Pencil className="w-4 h-4" />}>
              Modifier
            </Button>
          </Link>
        </div>
      </div>

      {/* Timeline visual */}
      <Card padding="sm">
        <div className="flex items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-brown-400" />
            <span className="text-sm text-brown-600">
              Cree le {formatDate(project.created_at)}
            </span>
          </div>
          <div className="flex-1 mx-8">
            <div className="h-1.5 bg-beige-200 rounded-full">
              <div
                className="h-1.5 bg-mint-500 rounded-full transition-all duration-500"
                style={{ width: `${project.progression}%` }}
              />
            </div>
          </div>
          <div className="text-right">
            <span className="text-lg font-semibold text-mint-600">
              {project.progression}%
            </span>
            <span className="text-sm text-brown-500 ml-1">complete</span>
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">
            <Flag className="w-4 h-4 mr-1.5" />
            Apercu
          </TabsTrigger>
          <TabsTrigger value="tasks">
            <LayoutList className="w-4 h-4 mr-1.5" />
            Taches ({project.tasks?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="documents">
            <FileText className="w-4 h-4 mr-1.5" />
            Documents ({project.documents?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="billing">
            <Receipt className="w-4 h-4 mr-1.5" />
            Facturation
          </TabsTrigger>
          <TabsTrigger value="design">
            <Palette className="w-4 h-4 mr-1.5" />
            Design
          </TabsTrigger>
          <TabsTrigger value="questions">
            <HelpCircle className="w-4 h-4 mr-1.5" />
            Questions
          </TabsTrigger>
          <TabsTrigger value="assistance">
            <Wrench className="w-4 h-4 mr-1.5" />
            Assistance ({assistanceRequests?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="members">
            <Users className="w-4 h-4 mr-1.5" />
            Membres
          </TabsTrigger>
          <TabsTrigger value="contrat">
            <ScrollText className="w-4 h-4 mr-1.5" />
            Contrat
          </TabsTrigger>
          <TabsTrigger value="activity">
            <Activity className="w-4 h-4 mr-1.5" />
            Activite
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab
            project={project}
            onStatusChange={handleStatusChange}
            onProgressionChange={handleProgressionChange}
          />
        </TabsContent>

        <TabsContent value="tasks">
          <TasksTab projectId={project.id} />
        </TabsContent>

        <TabsContent value="documents">
          <DocumentsTab projectId={project.id} />
        </TabsContent>

        <TabsContent value="billing">
          <InvoicesQuotesTab
            projectId={project.id}
            clientId={project.client_id}
            invoices={project.invoices}
            quotes={project.quotes}
          />
        </TabsContent>

        <TabsContent value="design">
          <DesignTab projectId={project.id} />
        </TabsContent>

        <TabsContent value="questions">
          <ProjectQuestionsTab
            project={{
              id: project.id,
              title: project.nom,
              user_id: project.client_id,
            }}
            clientProfile={project.client ? {
              id: project.client.id,
              full_name: project.client.full_name || 'Client',
              email: project.client.email || undefined,
            } : undefined}
          />
        </TabsContent>

        <TabsContent value="assistance">
          <AssistanceTab projectId={project.id} />
        </TabsContent>

        <TabsContent value="members">
          <MembersTab projectId={project.id} projectName={project.nom} />
        </TabsContent>

        <TabsContent value="contrat">
          <ContractTab projectId={project.id} clientId={project.client_id} />
        </TabsContent>

        <TabsContent value="activity">
          <ActivityTab projectId={project.id} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
